import React, { useState, useEffect } from 'react';

export const ProductQuickView = ({ product, attributes, onClose }) => {
    const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Reset selection when product changes
    useEffect(() => {
        if (product) {
            setSelectedVariantIndex(0);
            setSelectedImageIndex(0);
        }
    }, [product]);

    if (!product) return null;

    const currentVariant = product.variants[selectedVariantIndex];
    const currentImage = currentVariant.images[selectedImageIndex];
    
    // Resolve attributes
    const fabricText = attributes.fabrics[product.fabricId]?.join(', ') || product.fabricId;
    const fitText = attributes.fits[product.fitId] || product.fitId;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm transition-opacity" onClick={onClose}>
            <style>{`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
            {/* Modal Container */}
            <div 
                className="relative w-full max-w-5xl h-[90vh] bg-white dark:bg-zinc-900 rounded-lg shadow-2xl overflow-hidden flex flex-col md:flex-row"
                onClick={e => e.stopPropagation()} // Prevent closing when clicking inside
            >
                {/* Close Button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 bg-white/50 dark:bg-black/50 hover:bg-white dark:hover:bg-black rounded-full text-slate-900 dark:text-white transition-colors"
                    aria-label="Close modal"
                >
                    <span className="material-symbols-outlined text-lg">close</span>
                </button>

                {/* Left Side: Image Gallery */}
                <div className="w-full h-[50vh] md:h-full md:w-1/2 bg-slate-100 dark:bg-zinc-950 flex flex-col">
                    {/* Main Image */}
                    <div className="relative flex-1 w-full overflow-hidden">
                        <img 
                            src={currentImage.src} 
                            alt={currentImage.alt} 
                            className="w-full h-full object-cover"
                        />
                        {/* Image Navigation Arrows (if multiple images) */}
                        {currentVariant.images.length > 1 && (
                            <>
                                <button 
                                    onClick={() => setSelectedImageIndex(prev => prev === 0 ? currentVariant.images.length - 1 : prev - 1)}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/80 hover:bg-white dark:bg-black/80 dark:hover:bg-black rounded-full text-slate-900 dark:text-white transition-colors"
                                >
                                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                                </button>
                                <button 
                                    onClick={() => setSelectedImageIndex(prev => prev === currentVariant.images.length - 1 ? 0 : prev + 1)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/80 hover:bg-white dark:bg-black/80 dark:hover:bg-black rounded-full text-slate-900 dark:text-white transition-colors"
                                >
                                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                                </button>
                            </>
                        )}
                    </div>
                    {/* Thumbnails */}
                    {currentVariant.images.length > 1 && (
                        <div className="flex gap-2 p-4 bg-slate-50 dark:bg-zinc-900 overflow-x-auto hide-scrollbar">
                            {currentVariant.images.map((img, idx) => (
                                <button 
                                    key={idx}
                                    onClick={() => setSelectedImageIndex(idx)}
                                    className={`flex-shrink-0 w-16 h-20 border-2 transition-all ${selectedImageIndex === idx ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                >
                                    <img src={img.src} alt="thumbnail" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right Side: Product Details */}
                <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col overflow-y-auto h-full hide-scrollbar">
                    <div className="flex items-center gap-3 mb-2">
                        {product.bestSeller && (
                            <span className="bg-amber-500 text-white text-[10px] font-black px-2 py-1 uppercase tracking-widest rounded-sm">
                                Best Seller
                            </span>
                        )}
                        <span className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                            {product.patternNumber}
                        </span>
                    </div>

                    <h2 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase leading-tight mb-2">
                        {product.title}
                    </h2>
                    <p className="text-2xl font-display font-bold text-primary mb-6">
                        ₹{(product.price / 100).toFixed(2)}
                    </p>

                    <p className="text-slate-600 dark:text-slate-300 text-sm mb-8 leading-relaxed">
                        {product.description}
                    </p>

                    <div className="space-y-6 flex-1">
                        {/* Color Selector */}
                        <div>
                            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-3">
                                Color: <span className="text-slate-500 font-normal ml-1 capitalize">{currentVariant.color}</span>
                            </h3>
                            <div className="flex gap-3">
                                {product.variants.map((variant, idx) => (
                                    <button 
                                        key={idx}
                                        onClick={() => {
                                            setSelectedVariantIndex(idx);
                                            setSelectedImageIndex(0);
                                        }}
                                        className={`w-12 h-12 rounded-full border-2 p-0.5 transition-all ${selectedVariantIndex === idx ? 'border-primary' : 'border-slate-200 dark:border-slate-700 hover:border-slate-400'}`}
                                        aria-label={`Select color ${variant.color}`}
                                    >
                                        {/* Show the first image of the variant as a color swatch */}
                                        <div className="w-full h-full rounded-full overflow-hidden">
                                            <img src={variant.images[0].src} alt={variant.color} className="w-full h-full object-cover" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Sizes */}
                        <div>
                            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-3">
                                Available Sizes
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {currentVariant.sizes.map(size => (
                                    <div key={size} className="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold uppercase cursor-default">
                                        {size}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Specs */}
                        <div className="pt-6 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-4">
                            <div>
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Fit</h3>
                                <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">{fitText}</p>
                            </div>
                            <div>
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Fabric</h3>
                                <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">{fabricText}</p>
                            </div>
                            <div className="col-span-2">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Tags</h3>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {product.tags.map(tag => (
                                        <span key={tag} className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm">
                                            {tag.replace('-', ' ')}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
